import { TextField, InputAdornment, IconButton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

/**
 * SearchInput — Phase 2 reusable component.
 *
 * A styled, controlled search box. Deliberately does NOT debounce or call
 * any API itself — every existing page already owns its own search state
 * and its own decision about whether to filter client-side or call
 * `/search` endpoints (e.g. JobRequirements/Candidates use their own
 * `search`/`filterX` state). This component only standardizes the input's
 * look; the calling page keeps its exact existing search behavior when it
 * adopts this component in a later phase.
 *
 * Props:
 *   value        - current search string (required, controlled)
 *   onChange     - (newValue: string) => void (required)
 *   placeholder  - input placeholder (default: "Search...")
 *   onClear      - optional explicit clear handler; if omitted, the clear
 *                  button calls onChange("") directly
 *   size         - "small" | "medium" (default: "small")
 *   fullWidth    - default true
 */
export default function SearchInput({
  value,
  onChange,
  placeholder = "Search...",
  onClear,
  size = "small",
  fullWidth = true,
  sx = {},
}) {
  const handleClear = () => (onClear ? onClear() : onChange(""));

  return (
    <TextField
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      size={size}
      fullWidth={fullWidth}
      sx={sx}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
          </InputAdornment>
        ),
        endAdornment: value ? (
          <InputAdornment position="end">
            <IconButton size="small" onClick={handleClear} edge="end">
              <CloseIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  );
}
